import {DnsRecordChangeInput} from '../../dns-records/types';
import {
    DnsZone,
    ZoneCreateConnection,
} from '../types';

// FNP-1466 — DELEGATION, DERIVED RATHER THAN TYPED.
//
// A zone the product creates answers nothing until its PARENT carries an NS
// record pointing at the child's nameservers. Doing that by hand (FNP-1465,
// 2026-09-12) took four steps: read four nameservers off the child, switch to a
// connection that can write the parent, find the parent zone, and build the
// record. It works — and every step is a chance to paste the wrong string.
//
// ⚠️ THIS FILE IS THE WHOLE OF THE NEW LOGIC. There is no new mutation, no
// schema change and no port method: `applyDnsRecordChanges` is already wired,
// `NS` is already a supported type, and `nameservers` and `delegationParentName`
// are already selected on every zone. What was missing was only the derivation
// below — which record to write, and which connections may be offered to write
// it. Both are pure functions so they can be tested without a provider.

/**
 * The TTL a delegation record carries unless the operator changes it.
 *
 * ⚠️ THE CONVENTION, NOT AN INVENTION. Every existing delegation in
 * `dev.flexi.network` uses it — `billing.`, `marketplace.` and `stripe.`, read
 * live on 2026-09-12 — and 172800 (48h) is the usual value for a delegation
 * record, which changes rarely and is expensive to have cached wrongly.
 */
export const DEFAULT_DELEGATION_TTL_SECONDS = 172800;

/**
 * The NS record that delegates `zone` from its parent.
 *
 * Returns `undefined` when the record cannot be built honestly:
 *
 *   no nameservers — the child has not published any, so a delegation would
 *                    point nowhere. ⚠️ AN EMPTY NS RECORD IS WORSE THAN NO
 *                    RECORD: it is an assertion that the zone is served by
 *                    nothing, which a resolver will believe.
 *   no parent      — an apex, whose delegation lives at the registrar. There is
 *                    no zone inside the platform to write into.
 *
 * ⚠️ THE NAME IS THE CHILD'S, AND THE RECORD GOES IN THE PARENT'S ZONE. That
 * asymmetry is what a delegation IS, and it is the thing most easily got wrong
 * by hand: the record is named for the child but lives in the parent.
 */
export const delegationRecordFor = (
    zone: DnsZone,
    ttlSeconds: number = DEFAULT_DELEGATION_TTL_SECONDS,
): DnsRecordChangeInput | undefined => {

    const nameservers = zone.nameservers ?? [];

    if (nameservers.length === 0 || !zone.delegationParentName) {
        return undefined;
    }

    return {
        // ⚠️ UPSERT, NEVER A BARE CREATE. The schema has none, deliberately:
        // providers disagree about whether creating an existing record is an
        // error. Re-running a delegation must be idempotent rather than a
        // failure — an operator who is unsure whether it worked will try again.
        action: 'UPSERT',
        name: zone.name,
        type: 'NS',
        ttlSeconds,
        values: [...nameservers],
    };
};

/**
 * The change that REMOVES a zone's delegation from its parent.
 *
 * ⚠️ REMOVAL IS NOT MERELY THE UNDO OF ADDING, IT IS THE THING THAT PREVENTS A
 * LAME DELEGATION. A zone deleted while its parent still names its nameservers
 * leaves queries for that name hitting servers that no longer answer: they TIME
 * OUT rather than returning a clean NXDOMAIN, which is slower and harder to
 * diagnose than a name that simply does not exist. Worse, the stale NS record
 * keeps pointing at whatever is created with that name next.
 *
 * ⚠️ DELETE IDENTIFIES BY (name, type), BUT THE INPUT STILL CARRIES TTL AND
 * VALUES. They are required by `DnsRecordChangeInput` and are taken from the
 * record being removed rather than invented — the same rule
 * `useDeleteRecordMutation` follows next door. A DELETE whose values do not
 * match what exists is a change Route 53 will refuse.
 */
export const undelegationRecordFor = (
    zone: DnsZone,
    ttlSeconds: number = DEFAULT_DELEGATION_TTL_SECONDS,
): DnsRecordChangeInput | undefined => {

    const record = delegationRecordFor(zone, ttlSeconds);

    return record
        ? {
            ...record,
            action: 'DELETE',
        }
        : undefined;
};

/**
 * The connections that may be offered to write this zone's delegation.
 *
 * ⚠️ THE OPERATOR CHOOSES; THIS ONLY NARROWS. Owner decision, 2026-09-12. The
 * product must never pick a connection silently — FNP-1446 closed exactly that
 * defect, where a capability was resolved from an arbitrarily chosen connection
 * and the operator could not tell why a control behaved as it did.
 *
 * ⚠️ AND IT MUST NARROW, for the reason `useZoneCreateConnections` already
 * states about the create form: "if that picker offered every connection, a
 * tenant would choose a read-only one and get PERMISSION_DENIED at submit".
 * Offering an action certain to fail is the failure the hiding rule exists to
 * prevent, moved inside the form.
 *
 * Three tests, all of which must pass:
 *
 *   1. The connection REACHES THE PARENT ZONE. Checked by exact name match
 *      against the zone rows already loaded — every row carries the connection
 *      that read it, so this needs no new query.
 *   2. The connection is ACTIVE. A stored snapshot records what the credential
 *      proved at connect time; a non-ACTIVE status is the provider saying it
 *      does not work now.
 *   3. The credential declares `canWriteRecords`. Explicitly `=== true`: an
 *      absent snapshot must not pass.
 *
 * ⚠️ EXACT NAME, NEVER A SUFFIX. `flexi.network` is an ancestor of
 * `testing.dev.flexi.network` but is not its parent. Writing the delegation
 * into an ancestor creates a record in the wrong zone that silently does
 * nothing, and the operator would have no way to see why the name still does
 * not resolve.
 *
 * An empty result is the honest answer for the registrar case — the parent is
 * outside every connected account — and the caller shows the record to paste
 * rather than an error.
 */
export const parentWriteCandidates = (
    zone: DnsZone,
    // Every zone row the list has loaded, one per connection that reached it.
    // `useZones().rawItems`, not the grouped rows: the per-connection reads are
    // exactly what this needs.
    loadedZones: readonly DnsZone[],
    connections: readonly ZoneCreateConnection[],
): ZoneCreateConnection[] => {

    const parentName = zone.delegationParentName;

    if (!parentName) {
        return [];
    }

    const reachesParent = new Set(
        loadedZones
            .filter(candidate => candidate.name === parentName)
            .map(candidate => candidate.connectionId),
    );

    return connections.filter(
        connection =>
            reachesParent.has(connection.id)
            && connection.status === 'ACTIVE'
            && connection.capabilities?.canWriteRecords === true,
    );
};
